import { DataSubType, DataType, SchemaField } from "oschema";

/**
 * The API hands us `DatabaseFieldDto`s cast to `SchemaField`. The DTO carries a
 * few extra columns that `oschema`'s published type does not model yet, and the
 * UI needs them (primary keys must be read-only, enums want a Select, and so
 * on). `Field` is the shape we actually receive at runtime.
 */
export type Field = SchemaField & {
  primary?: boolean | null;
  unique?: boolean | null;
  indexed?: boolean | null;
  isEnum?: boolean | null;
  enumClosed?: boolean | null;
  default?: string | null;
};

const ACRONYMS = new Set([
  "ID",
  "IDS",
  "URL",
  "URI",
  "UUID",
  "API",
  "HTML",
  "JSON",
  "CSV",
  "IP",
  "MAC",
  "SQL",
  "PDF",
  "SKU",
  "KPI",
  "UTC",
]);

/** snake_case / camelCase / kebab-case -> "Title Case". */
export const humanize = (raw?: string): string => {
  if (!raw) return "";
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .trim()
    .split(/\s+/)
    .map((word) => {
      // Keep well-known acronyms upright rather than "Id" / "Url" / "Uuid".
      const upper = word.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

export const fieldLabel = (field: Field): string =>
  humanize(field.label || field.name);

/**
 * "projects" -> "Project". Table names are plural and a modal titled "Edit
 * Projects" reads as though it edits all of them.
 */
export const singularize = (raw: string): string => {
  const word = humanize(raw);
  if (/(ss|us|is|s's)$/i.test(word)) return word;
  if (/ies$/i.test(word)) return word.replace(/ies$/i, "y");
  if (/(ch|sh|x|z|s)es$/i.test(word)) return word.replace(/es$/i, "");
  if (/s$/i.test(word)) return word.replace(/s$/i, "");
  return word;
};

const ID_NAMES = new Set(["id", "_id", "uuid", "guid", "pk"]);

export const isPrimaryKey = (field: Field): boolean => {
  if (field.primary) return true;
  return ID_NAMES.has(field.name.toLowerCase());
};

const GENERATED_NAMES = /^(created|updated|deleted|modified)_?(at|on|date|time)$/i;

/** Columns named like audit timestamps (`created_at`, `updated_on`, …). */
export const isGenerated = (field: Field): boolean =>
  GENERATED_NAMES.test(field.name);

/**
 * An audit-named column the database will fill: it has a default, or it
 * accepts NULL. A NOT NULL audit column with no default is not generated.
 */
export const isDatabaseGenerated = (field: Field): boolean =>
  isGenerated(field) && (field.default != null || field.nullable === true);

/** Fields the user may not type into on update / inline-edit / bulk / csv. */
export const isReadOnlyField = (field: Field): boolean =>
  isPrimaryKey(field) || isGenerated(field);

/**
 * Create-mode lock. Primary keys stay off the form; audit columns are writable
 * only when the database will not fill them (NOT NULL, no default).
 */
export const isReadOnlyOnCreate = (field: Field): boolean =>
  isPrimaryKey(field) || isDatabaseGenerated(field);

/**
 * Writable columns from `row` for a create-mode prefill. Primary keys and
 * generated audit timestamps stay off the copy so the form is not treated as
 * an update of the source record.
 */
export const copyRowForCreate = (
  row: Record<string, unknown> | null | undefined,
  fields: Field[] = []
): Record<string, unknown> => {
  if (!row) return {};
  const out: Record<string, unknown> = {};
  if (fields.length) {
    for (const field of fields) {
      if (isReadOnlyField(field)) continue;
      if (!Object.prototype.hasOwnProperty.call(row, field.name)) continue;
      out[field.name] = row[field.name];
    }
    return out;
  }
  for (const [key, value] of Object.entries(row)) {
    if (key.startsWith("_")) continue;
    if (ID_NAMES.has(key.toLowerCase()) || GENERATED_NAMES.test(key)) continue;
    out[key] = value;
  }
  return out;
};

const VOLATILE_DEFAULT =
  /^(nextval|now|gen_random_uuid|uuid_generate_v4|clock_timestamp|statement_timestamp|transaction_timestamp|random|current_timestamp|current_date|current_time|localtimestamp|localtime|session_user|current_user|current_role|current_catalog|current_schema)\b/i;

const unwrapDefaultParens = (raw: string): string => {
  let s = raw;
  while (s.startsWith("(") && s.endsWith(")")) {
    const inner = s.slice(1, -1).trim();
    if (!inner) return "";
    s = inner;
  }
  return s;
};

/**
 * Postgres `column_default` as a typed form value, or `undefined` when it is
 * not a literal (sequences, `now()`, uuid generators, expressions).
 *
 * `'pending'::character varying` -> `"pending"`; `NULL` -> `null`; `true`/`0`
 * stay boolean/number. Never treat `nextval` / `now()` as something the user
 * typed.
 */
export const parseColumnDefault = (
  raw?: string | null
): string | number | boolean | null | undefined => {
  if (raw == null) return undefined;
  const s = unwrapDefaultParens(String(raw).trim());
  if (!s) return undefined;

  if (VOLATILE_DEFAULT.test(s) || /^[A-Za-z_][\w$]*\s*\(/.test(s)) {
    return undefined;
  }

  const uncast = s.replace(/\s*::[\s\S]+$/, "").trim();
  if (!uncast) return undefined;
  if (/^null$/i.test(uncast)) return null;
  if (/^true$/i.test(uncast)) return true;
  if (/^false$/i.test(uncast)) return false;
  if (/^[+-]?\d+(\.\d+)?$/.test(uncast)) return Number(uncast);

  const quoted = s.match(/^E?'((?:[^']|'')*)'\s*(::[\s\S]*)?$/i);
  if (quoted) return quoted[1].replace(/''/g, "'");

  return undefined;
};

/** Parseable column defaults for a create form. Read-only columns stay out. */
export const defaultsForCreate = (
  fields: Field[] = []
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    if (isReadOnlyOnCreate(field)) continue;
    const parsed = parseColumnDefault(field.default);
    if (parsed !== undefined) {
      out[field.name] = parsed;
      continue;
    }
    // NOT NULL audit timestamps with no default: seed "now" so create POSTs
    // a value. Non-DATE audit names stay empty and fail required validation.
    if (isGenerated(field) && field.type === DataType.DATE) {
      out[field.name] = nowInputValue(field);
    }
  }
  return out;
};

export const isTemporal = (field?: Field): boolean => {
  if (!field) return false;
  if (field.type === DataType.DATE) return true;
  switch (field.subType) {
    case DataSubType.DATE:
    case DataSubType.TIME:
    case DataSubType.TIMESTAMP:
    case DataSubType.DATETIME_WITH_TIMEZONE:
    case DataSubType.DATETIME_WITHOUT_TIMEZONE:
      return true;
    default:
      return false;
  }
};

const NUMERIC_SUBTYPES = new Set<string>([
  DataSubType.INTEGER,
  DataSubType.FLOAT,
  DataSubType.DECIMAL,
  DataSubType.PERCENTAGE,
  DataSubType.CURRENCY,
]);

/**
 * Whether the field reads as a number. Postgres hands `numeric`/`money` columns
 * back as JS strings, so the analyser types them STRING with a numeric subType;
 * refusing those would leave every currency column left-aligned and unformatted.
 */
export const isNumeric = (field?: Field): boolean => {
  if (!field) return false;
  if (field.type === DataType.NUMBER) return true;
  if (field.type && field.type !== DataType.STRING) return false;
  return !!field.subType && NUMERIC_SUBTYPES.has(field.subType);
};

/**
 * Whether the *column* is genuinely numeric, as opposed to a string column the
 * analyser guessed a numeric subType for. Only these get a `type="number"`
 * input: a phone column typed STRING/INTEGER would silently blank "+1 415…".
 */
export const isNativeNumber = (field?: Field): boolean =>
  field?.type === DataType.NUMBER;

/**
 * Whether the column really is a date, as opposed to a string column the
 * analyser guessed a temporal subType for. Only these get a native picker.
 */
export const isNativeDate = (field?: Field): boolean =>
  field?.type === DataType.DATE;

/** Strict enough to reject "+14155552671" and "1-2", unlike `Number()`. */
export const parsesAsNumber = (value: unknown): boolean => {
  if (typeof value === "number") return !isNaN(value);
  if (typeof value !== "string") return false;
  return /^-?\d+(\.\d+)?$/.test(value.trim());
};

export const isBooleanField = (field?: Field): boolean =>
  field?.type === DataType.BOOLEAN || field?.subType === DataSubType.BOOLEAN;

export const isStructured = (field?: Field): boolean =>
  field?.type === DataType.OBJECT || field?.subType === DataSubType.JSON;

const SECRET_NAME = /(password|passwd|secret|token|api[_-]?key|^hashed_)/i;

/** Fields whose values must never be displayed on read-only surfaces. */
export const isSecretField = (
  field?: Pick<Field, "name" | "subType"> | string
): boolean => {
  if (!field) return false;
  if (typeof field === "string") return SECRET_NAME.test(field);
  return (
    field.subType === DataSubType.PASSWORD || SECRET_NAME.test(field.name)
  );
};

const LONG_TEXT_SUBTYPES = new Set<string>([
  DataSubType.TEXT,
  DataSubType.HTML,
  DataSubType.MARKDOWN,
  DataSubType.ADDRESS,
  DataSubType.BASE64,
]);

/**
 * Conventional prose columns should not collapse to a single-line input just
 * because the analyser happened to sample only short values. Keep this list
 * deliberately semantic and exact: a field such as `content_type` is metadata,
 * not necessarily prose.
 */
const LONG_TEXT_NAMES = new Set([
  "description",
  "notes",
  "note",
  "body",
  "content",
  "comment",
  "bio",
  "summary",
  "message",
]);

export const isLongText = (field?: Field): boolean => {
  if (!field) return false;
  if (isStructured(field)) return true;
  // Names are only a presentation hint for real string columns. A numeric or
  // temporal `description` must retain its lossless native editor.
  if (
    field.type === DataType.STRING &&
    LONG_TEXT_NAMES.has(field.name.trim().toLowerCase())
  ) {
    return true;
  }
  if (field.subType && LONG_TEXT_SUBTYPES.has(field.subType)) {
    // TEXT is handed out generously by the analyser; only treat it as long-form
    // when the sampled values actually were.
    if (field.subType === DataSubType.TEXT) return (field.maxLength ?? 0) > 60;
    return true;
  }
  return (field.maxLength ?? 0) > 255;
};

export const enumOptions = (field?: Field): string[] | undefined => {
  if (!field) return undefined;
  if (field.isEnum === false) return undefined;
  // A headcount column with nine distinct values sampled is not an enum, and
  // neither is a time column with a handful of distinct clock times. Only
  // string-shaped columns get a picker.
  if (isNumeric(field) || isTemporal(field) || isBooleanField(field)) {
    return undefined;
  }
  const values = field.enum;
  if (!Array.isArray(values) || values.length === 0) return undefined;
  // An "enum" of 200 sampled values is not an enum, it is a text column.
  if (values.length > 40) return undefined;
  return values;
};

/**
 * The one place that decides which editor a field gets. Keeping this separate
 * from the rendering means the table, the form and the filter popover agree on
 * what a column is.
 */
export type InputKind =
  | "boolean"
  | "enum"
  | "number"
  | "currency"
  | "percentage"
  | "date"
  | "datetime"
  | "time"
  | "json"
  | "longtext"
  | "color"
  | "email"
  | "url"
  | "phone"
  | "password"
  | "text";

export const inputKindFor = (field: Field): InputKind => {
  if (isBooleanField(field)) return "boolean";
  if (isStructured(field)) return "json";
  // A colour column with five distinct hex values sampled is still a colour.
  if (field.subType === DataSubType.COLOR) return "color";
  if (enumOptions(field)) return "enum";

  if (isTemporal(field)) {
    switch (field.subType) {
      case DataSubType.TIME:
        return "time";
      case DataSubType.DATE:
        return "date";
      case DataSubType.TIMESTAMP:
      case DataSubType.DATETIME_WITH_TIMEZONE:
      case DataSubType.DATETIME_WITHOUT_TIMEZONE:
        return "datetime";
      default:
        // No subType: `starts_on` / `due_date` are calendar dates, `*_at` is a
        // timestamp. Guessing datetime for everything showed a time picker on
        // every plain date column.
        return /(_on|_date|^date$)$/i.test(field.name) ? "date" : "datetime";
    }
  }

  if (isNumeric(field)) {
    if (field.subType === DataSubType.CURRENCY) return "currency";
    if (field.subType === DataSubType.PERCENTAGE) return "percentage";
    // Analyser often leaves money columns as plain NUMBER. Snake-case tokens
    // are the word boundary here: JS `\b` treats `_` as a word character, so
    // `actual_cost` would miss a `\bcost\b` test.
    if (/(^|_)(budget|cost|price|amount|fee|revenue)s?($|_)/i.test(field.name)) {
      return "currency";
    }
    // Same gap as money: `progress_percentage` is often plain NUMBER.
    if (/(^|_)percent(age)?s?($|_)/i.test(field.name)) {
      return "percentage";
    }
    return "number";
  }

  switch (field.subType) {
    case DataSubType.EMAIL:
      return "email";
    case DataSubType.URL:
    case DataSubType.IMAGE_URL:
    case DataSubType.VIDEO_URL:
    case DataSubType.AUDIO_URL:
      // Deliberately still a plain text input under the hood: the analyser tags
      // short strings like "In Progress" as URL constantly, and `type="url"`
      // would reject them. The url kind only adds an "open link" affordance,
      // and only when the current value actually parses as one.
      return "url";
    case DataSubType.PHONE_NUMBER:
    case DataSubType.PHONE_NUMBER_WITH_CODE:
    case DataSubType.PHONE_NUMBER_WITH_COUNTRY_CODE:
      return "phone";
    case DataSubType.PASSWORD:
      return "password";
    default:
      break;
  }

  return isLongText(field) ? "longtext" : "text";
};

const padDatePart = (n: number) => String(n).padStart(2, "0");

/**
 * Current local time in the same shape as the native date / datetime-local /
 * time inputs (`toDateInput` / `toDateTimeInput` / `toTimeInput`).
 */
export const nowInputValue = (field: Field): string => {
  const d = new Date();
  const date = `${d.getFullYear()}-${padDatePart(d.getMonth() + 1)}-${padDatePart(
    d.getDate()
  )}`;
  const time = `${padDatePart(d.getHours())}:${padDatePart(d.getMinutes())}`;
  const kind = inputKindFor(field);
  if (kind === "date") return date;
  if (kind === "time") return time;
  return `${date}T${time}`;
};

/** Lenient link test used for adornments and cell links. */
export const looksLikeUrl = (value: unknown): boolean =>
  typeof value === "string" && /^(https?:)?\/\/\S+$/i.test(value.trim());

export const looksLikeEmail = (value: unknown): boolean =>
  typeof value === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const IDENTITY_NAME =
  /^(name|title|label|slug|code|key|display_?name|full_?name|username|email|subject|headline)$/i;
const STATE_NAME =
  /(^|_)(status|state|stage|phase|priority|severity|type|kind|category|level|tier)($|_)/i;

/**
 * Enrich state-like string columns with the distinct values visible on the
 * current page. The analyser can explicitly reject an enum even when the
 * loaded rows show a useful finite state set, so these copies opt back into
 * the existing enum editor without changing the analyser-owned schema.
 */
export const withObservedEnums = (
  fields: Field[] = [],
  rows: Array<Record<string, unknown>> = []
): Field[] =>
  fields.map((field) => {
    if (
      !STATE_NAME.test(field.name) ||
      enumOptions(field) ||
      isNumeric(field) ||
      isTemporal(field) ||
      isBooleanField(field) ||
      isStructured(field)
    ) {
      return field;
    }

    const observed = new Set<string>();
    for (const row of rows) {
      const value = row?.[field.name];
      if (
        typeof value !== "string" ||
        value.trim() === "" ||
        value === "null"
      ) {
        continue;
      }
      observed.add(value);
      if (observed.size > 40) return field;
    }

    if (observed.size === 0) return field;
    return { ...field, isEnum: true, enum: Array.from(observed) };
  });

export const isIdentityField = (field: Field): boolean =>
  IDENTITY_NAME.test(field.name);

const IDENTITY_DISPLAY_RANK = (name: string): number => {
  const n = name.toLowerCase().replace(/_/g, "");
  const order = [
    "name",
    "title",
    "label",
    "slug",
    "displayname",
    "fullname",
    "username",
    "email",
    "subject",
    "headline",
    "code",
    "key",
  ];
  const i = order.indexOf(n);
  return i === -1 ? order.length : i;
};

export const primaryFieldName = (
  fields?: Array<{ name: string; primary?: boolean | null }> | null
): string => fields?.find((field) => field.primary)?.name ?? "id";

/**
 * Identity columns that can take a `contains` filter. Type beats subtype: a
 * NUMBER/DATE/BOOLEAN/`json` column is skipped even if its name looks like
 * `code` or `key`.
 */
export const searchableIdentityNames = (fields?: Field[] | null): string[] =>
  (fields ?? [])
    .filter(isIdentityField)
    .filter(
      (field) =>
        field.type !== DataType.NUMBER &&
        field.type !== DataType.BOOLEAN &&
        field.type !== DataType.DATE &&
        !isStructured(field)
    )
    .map((field) => field.name);

export type ContainsFilter = {
  field: string;
  operator: "contains";
  value: string;
};

/**
 * Free-text search for a relation picker or a phone list.
 *
 * Identity columns alone miss the row a person actually has: an order number,
 * a customer id, the `#42` the grid already prints. The primary key is always
 * OR-ed in. A leading `#` is stripped only for that key, so `#42` matches id
 * 42 and does not require a name to contain a hash. Numeric keys are safe:
 * the API casts `contains` to text before ILIKE.
 *
 * Blank input returns undefined so the caller keeps the unfiltered first page.
 */
export const searchFiltersFor = (
  raw: string,
  identityFields: readonly string[],
  pkName?: string | null
): ContainsFilter[] | undefined => {
  const needle = raw.trim();
  if (!needle) return undefined;
  const idNeedle = needle.replace(/^#/, "");
  const filters: ContainsFilter[] = [];
  const seen = new Set<string>();
  for (const field of identityFields) {
    if (!field || field === pkName || seen.has(field)) continue;
    seen.add(field);
    filters.push({ field, operator: "contains", value: needle });
  }
  if (pkName && idNeedle && !seen.has(pkName)) {
    seen.add(pkName);
    filters.push({ field: pkName, operator: "contains", value: idNeedle });
  }
  return filters.length ? filters : undefined;
};

/**
 * First non-empty identity-like value (`name`, `title`, `label`, `slug`, …),
 * then `#id`. Schema fields, when given, bound the candidate set to columns
 * that actually exist.
 */
export const rowLabel = (
  row: Record<string, unknown> | null | undefined,
  opts?: { fields?: Field[] | null; pkName?: string }
): string => {
  if (!row || typeof row !== "object") return "Record";
  const pkName = opts?.pkName ?? "id";

  const names = opts?.fields?.length
    ? opts.fields.filter(isIdentityField).map((field) => field.name)
    : Object.keys(row).filter((key) => IDENTITY_NAME.test(key));

  names.sort(
    (a, b) =>
      IDENTITY_DISPLAY_RANK(a) - IDENTITY_DISPLAY_RANK(b) || a.localeCompare(b)
  );

  for (const key of names) {
    const candidate = row[key];
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      return String(candidate);
    }
  }

  const id = row[pkName] ?? row.id;
  if (id != null && id !== "") return `#${id}`;
  return "Record";
};

/**
 * Columns the user must fill on create. A toggle always has a value, and a
 * column the database defaults does not need one from the form.
 */
export const isRequired = (field: Field): boolean =>
  field.nullable === false &&
  field.default == null &&
  inputKindFor(field) !== "boolean";

/**
 * Default column order. The API returns fields in whatever order the sampler
 * happened to build its map, which puts a JSON blob before the row's name as
 * often as not. This groups them the way someone scanning a table expects:
 * what the row *is*, what state it is in, its other values, its long-form text,
 * structured values, the primary key, and finally the audit timestamps.
 */
export const columnRank = (field: Field): number => {
  if (isPrimaryKey(field)) return 5;
  if (isIdentityField(field)) return 0;
  if (STATE_NAME.test(field.name)) return 1;
  if (isGenerated(field)) return 6;
  if (isStructured(field)) return 4;
  if (isLongText(field)) return 3;
  return 2;
};

/**
 * Phone card: identity title plus the next two ranked scalars. PKs, secrets,
 * JSON, and audit timestamps stay off the card — they belong in RowDetailModal.
 */
export const mobileCardFields = (
  schema: Field[]
): { title?: Field; extras: Field[] } => {
  const candidates = schema.filter(
    (field) =>
      !isPrimaryKey(field) &&
      !isSecretField(field) &&
      !isStructured(field) &&
      !isGenerated(field)
  );
  const ranked = [...candidates].sort((a, b) => {
    const delta = columnRank(a) - columnRank(b);
    if (delta !== 0) return delta;
    return a.name.localeCompare(b.name);
  });
  const title = ranked.find(isIdentityField) ?? ranked[0];
  const extras = ranked.filter((field) => field !== title).slice(0, 2);
  return { title, extras };
};

/**
 * The collection create drawer leads with the values that describe the row:
 * identity, required values, state, other scalars (including dates and FKs),
 * and prose. Exclusions win even when a field is required or identity-named;
 * audit columns, structured data and secrets belong in the extras disclosure.
 * This is presentation only: create locks and validation remain independent.
 */
export const isCreateDrawerPrimary = (field: Field): boolean => {
  if (
    isPrimaryKey(field) ||
    isGenerated(field) ||
    isStructured(field) ||
    isSecretField(field)
  ) {
    return false;
  }
  return isIdentityField(field) || isRequired(field) || columnRank(field) <= 3;
};
